/**
 * Centralized Error Logging System
 *
 * Provides structured logging with different severity levels and context
 */

import { safeGetItem, safeRemoveItem, safeSetItem } from '@/utils/safeStorage';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'fatal';

export interface LogContext {
  userId?: string;
  component?: string;
  action?: string;
  metadata?: Record<string, unknown>;
}

export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  error?: Error | string;
  context?: LogContext;
  stack?: string;
}

class ErrorLogger {
  private isDevelopment = import.meta.env.DEV;
  private logs: LogEntry[] = [];
  private maxLogs = 1000; // Keep last 1000 logs in memory

  /**
   * Log an error with context
   */
  error(message: string, error?: Error | string, context?: LogContext): void {
    this.log('error', message, error, context);
  }

  /**
   * Log a warning
   */
  warn(message: string, context?: LogContext): void {
    this.log('warn', message, undefined, context);
  }

  /**
   * Log information (development only)
   */
  info(message: string, context?: LogContext): void {
    if (this.isDevelopment) {
      this.log('info', message, undefined, context);
    }
  }

  /**
   * Log debug information (development only)
   */
  debug(message: string, context?: LogContext): void {
    if (this.isDevelopment) {
      this.log('debug', message, undefined, context);
    }
  }

  /**
   * Log fatal errors (always logged)
   */
  fatal(message: string, error?: Error | string, context?: LogContext): void {
    this.log('fatal', message, error, context);
  }

  /**
   * Core logging method
   */
  private log(level: LogLevel, message: string, error?: Error | string, context?: LogContext): void {
    const logEntry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      error: error?.toString(),
      context,
      stack: error instanceof Error ? error.stack : undefined,
    };

    // Add to memory store
    this.logs.push(logEntry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    // Console output in development
    if (this.isDevelopment) {
      this.logToConsole(logEntry);
    }

    // Send to monitoring service in production
    if (!this.isDevelopment && (level === 'error' || level === 'fatal')) {
      this.sendToMonitoring(logEntry);
    }
  }

  /**
   * Console output with proper formatting
   */
  private logToConsole(entry: LogEntry): void {
    const { level, message, error, context } = entry;
    const prefix = `[${entry.timestamp}] [${level.toUpperCase()}]`;

    switch (level) {
      case 'debug':
        console.debug(prefix, message, context);
        break;
      case 'info':
        console.info(prefix, message, context);
        break;
      case 'warn':
        console.warn(prefix, message, context);
        break;
      case 'error':
      case 'fatal':
        console.error(prefix, message);
        if (error) console.error('Error details:', error);
        if (context) console.error('Context:', context);
        break;
    }
  }

  /**
   * Send critical errors to monitoring service (uses safe storage for Safari/iOS compatibility)
   */
  private sendToMonitoring(entry: LogEntry): void {
    try {
      const existingLogs = JSON.parse(safeGetItem('app_error_logs') || '[]');
      existingLogs.push(entry);

      if (existingLogs.length > 50) {
        existingLogs.splice(0, existingLogs.length - 50);
      }

      safeSetItem('app_error_logs', JSON.stringify(existingLogs));
    } catch (e) {
      console.error('Failed to store error log:', e);
    }
  }

  /**
   * Get recent logs (for debugging)
   */
  getRecentLogs(count = 50): LogEntry[] {
    return this.logs.slice(-count);
  }

  /**
   * Clear all logs
   */
  clearLogs(): void {
    this.logs = [];
    safeRemoveItem('app_error_logs');
  }

  /**
   * Get logs by level
   */
  getLogsByLevel(level: LogLevel): LogEntry[] {
    return this.logs.filter((log) => log.level === level);
  }
}

const logger = new ErrorLogger();

// Convenience functions for common logging patterns
export const logError = (message: string, error?: Error | string, context?: LogContext) =>
  logger.error(message, error, context);

export const logWarning = (message: string, context?: LogContext) => logger.warn(message, context);

export const logDebug = (message: string, context?: LogContext) => logger.debug(message, context);
